import { useState, useEffect, useMemo } from "react";

import { TextField, TextStyle, Label, InputType } from "@lannister/duplo";

const RequiredDetails = ({ schema, onDataChange, setIsValid }) => {
    const [customerData, setCustomerData] = useState({});
    const [touched, setTouched] = useState({});

    const mapType = (fieldType) => {
        return fieldType === "string" ? InputType.Text : InputType.Number;
    }

    const fields = useMemo(() => {
        if (!schema?.properties) return [];
        return Object.entries(schema.properties).map(([key, value]) => ({
            key,
            label: value.title,
            required: schema.required.includes(key),
            type: value.type,
        }));
    }, [schema]);

    const requiredKeys = useMemo(
        () => fields.filter(f => f.required).map(f => f.key),
        [fields]
    );

    useEffect(() => {
        const allFilled = requiredKeys.every(key => customerData[key]?.trim().length > 0);
        setIsValid(allFilled);
    }, [requiredKeys, customerData, setIsValid]);

    const handleChange = (field, value) => {
        const updated = { ...customerData, [field]: value };
        setCustomerData(updated);
        onDataChange(field, value);

        const allFilled = requiredKeys.every(key => updated[key]?.trim().length > 0);
        setIsValid(allFilled);
    }

    const handleBlur = (fieldKey) => {
        setTouched(prev => ({ ...prev, [fieldKey]: true }));
    }

    const showError = (field) => {
        if (!touched[field.key]) return undefined;
        if (field.required && !customerData[field.key]?.trim()) 
            return `${field.label} is required`;
        return undefined;
    };

    const renderField = (field, fullWidth = false) => {
        return (
            <div key={field.key} style={{ ...styles.fieldWrapper, ...(fullWidth && styles.fullWidth) }}>
                <TextField
                    value={customerData[field.key]}
                    label={`${field.label}${!field.required ? " (Optional)" : ""}`}
                    onChange={(e) => handleChange(field.key, e.target.value)}
                    type={mapType(field.type)}
                    errorMessage={showError(field)}
                    onBlur={() => handleBlur(field.key)}
                />
            </div>
        );
    };

    const renderRows = () => {
        const rows = [];
        for (let i = 0; i < fields.length; i += 2) {
            const isLast = i === fields.length - 1;
            rows.push(
                <div key={i} style={styles.row}>
                    {isLast
                        ? renderField(fields[i], true)
                        : <>
                            {renderField(fields[i])}
                            {renderField(fields[i + 1])}
                          </>
                    }
                </div>
            );
        }
        return rows;
    };

    return (
        <div style={styles.card}>
            <Label labelText="Required Details" textStyle={TextStyle.Heading5Semibold} />
            <div style={styles.grid}>
                {renderRows()}
            </div>
        </div>
    );
};

const styles = {
    card: {
        display: "flex",
        padding: "16px",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "16px",
        alignSelf: "stretch",
        borderRadius: "16px",
        border: "1px solid #E6E8FA",
        background: "#FFF",
    },
    grid: {
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        alignSelf: "stretch",
    },
    row: {
        display: "flex",
        gap: "16px",
        alignSelf: "stretch",
    },
    fieldWrapper: {
        flex: 1,
    },
    fullWidth: {
        flex: "unset",
        width: "100%",
    },
};

export default RequiredDetails;